/**
 * Audio utilities for WebSocket-based voice calling
 * Handles µ-law encoding/decoding and WAV file generation
 */

/**
 * µ-law decode table - maps µ-law byte values to 16-bit PCM values
 * Used to decode audio from server
 */
export const MULAW_DECODE_TABLE = new Int16Array([
  -32124, -31100, -30076, -29052, -28028, -27004, -25980, -24956,
  -23932, -22908, -21884, -20860, -19836, -18812, -17788, -16764,
  -15996, -15484, -14972, -14460, -13948, -13436, -12924, -12412,
  -11900, -11388, -10876, -10364, -9852, -9340, -8828, -8316,
  -7932, -7676, -7420, -7164, -6908, -6652, -6396, -6140,
  -5884, -5628, -5372, -5116, -4860, -4604, -4348, -4092,
  -3900, -3772, -3644, -3516, -3388, -3260, -3132, -3004,
  -2876, -2748, -2620, -2492, -2364, -2236, -2108, -1980,
  -1884, -1820, -1756, -1692, -1628, -1564, -1500, -1436,
  -1372, -1308, -1244, -1180, -1116, -1052, -988, -924,
  -876, -844, -812, -780, -748, -716, -684, -652,
  -620, -588, -556, -524, -492, -460, -428, -396,
  -372, -356, -340, -324, -308, -292, -276, -260,
  -244, -228, -212, -196, -180, -164, -148, -132,
  -120, -112, -104, -96, -88, -80, -72, -64,
  -56, -48, -40, -32, -24, -16, -8, 0,
  32124, 31100, 30076, 29052, 28028, 27004, 25980, 24956,
  23932, 22908, 21884, 20860, 19836, 18812, 17788, 16764,
  15996, 15484, 14972, 14460, 13948, 13436, 12924, 12412,
  11900, 11388, 10876, 10364, 9852, 9340, 8828, 8316,
  7932, 7676, 7420, 7164, 6908, 6652, 6396, 6140,
  5884, 5628, 5372, 5116, 4860, 4604, 4348, 4092,
  3900, 3772, 3644, 3516, 3388, 3260, 3132, 3004,
  2876, 2748, 2620, 2492, 2364, 2236, 2108, 1980,
  1884, 1820, 1756, 1692, 1628, 1564, 1500, 1436,
  1372, 1308, 1244, 1180, 1116, 1052, 988, 924,
  876, 844, 812, 780, 748, 716, 684, 652,
  620, 588, 556, 524, 492, 460, 428, 396,
  372, 356, 340, 324, 308, 292, 276, 260,
  244, 228, 212, 196, 180, 164, 148, 132,
  120, 112, 104, 96, 88, 80, 72, 64,
  56, 48, 40, 32, 24, 16, 8, 0
]);

/**
 * µ-law encode table - maps 14-bit linear values to µ-law byte values
 * Used to encode audio for sending to server (if needed)
 */
export const MULAW_ENCODE_TABLE = new Uint8Array(16384);

// Initialize encode table
(function initMulawEncodeTable() {
  for (let i = 0; i < 256; i++) {
    const decoded = MULAW_DECODE_TABLE[i];
    const linear = Math.floor((decoded + 32768) / 4);
    if (linear >= 0 && linear < 16384) {
      MULAW_ENCODE_TABLE[linear] = i;
    }
  }
})();

/**
 * Process audio payload from server - decode µ-law base64 to Int16 PCM
 * @param payload - Base64 encoded µ-law audio data
 * @returns Int16Array of PCM audio samples
 */
export function processAudioPayload(payload: string): Int16Array {
  // Decode base64 to bytes
  const binaryString = atob(payload);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Decode µ-law to PCM
  const pcm = new Int16Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    pcm[i] = MULAW_DECODE_TABLE[bytes[i]];
  }

  return pcm;
}

/**
 * Convert Float32 audio samples to Int16 PCM
 * @param float32Samples - Float32 audio samples
 * @returns Int16Array of PCM samples
 */
export function float32ToInt16(float32Samples: Float32Array): Int16Array {
  const int16 = new Int16Array(float32Samples.length);
  for (let i = 0; i < float32Samples.length; i++) {
    const sample = Math.max(-1, Math.min(1, float32Samples[i]));
    int16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
  }
  return int16;
}

/**
 * Convert Int16 PCM samples to Float32 for Web Audio API
 * @param int16Samples - Int16 PCM samples
 * @returns Float32Array suitable for AudioBuffer
 */
export function int16ToFloat32(int16Samples: Int16Array): Float32Array {
  const float32 = new Float32Array(int16Samples.length);
  for (let i = 0; i < int16Samples.length; i++) {
    float32[i] = int16Samples[i] / (int16Samples[i] < 0 ? 0x8000 : 0x7FFF);
  }
  return float32;
}

/**
 * Encode PCM audio to WAV format for recording
 * @param pcmBuffer - Array of Int16 PCM buffers
 * @param sampleRate - Sample rate (default: 8000)
 * @returns Blob containing WAV file
 */
export function encodeWAV(pcmBuffer: Int16Array[], sampleRate: number = 8000): Blob {
  // Calculate total length
  let totalLength = 0;
  for (const buffer of pcmBuffer) {
    totalLength += buffer.length;
  }

  // Combine all buffers
  const combinedBuffer = new Int16Array(totalLength);
  let offset = 0;
  for (const buffer of pcmBuffer) {
    combinedBuffer.set(buffer, offset);
    offset += buffer.length;
  }

  // Create WAV file
  const buffer = new ArrayBuffer(44 + combinedBuffer.length * 2);
  const view = new DataView(buffer);

  // WAV header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + combinedBuffer.length * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
  view.setUint16(20, 1, true); // AudioFormat (1 for PCM)
  view.setUint16(22, 1, true); // NumChannels (mono)
  view.setUint32(24, sampleRate, true); // SampleRate
  view.setUint32(28, sampleRate * 2, true); // ByteRate
  view.setUint16(32, 2, true); // BlockAlign
  view.setUint16(34, 16, true); // BitsPerSample
  writeString(view, 36, 'data');
  view.setUint32(40, combinedBuffer.length * 2, true);

  // Write PCM data
  const dataOffset = 44;
  for (let i = 0; i < combinedBuffer.length; i++) {
    view.setInt16(dataOffset + i * 2, combinedBuffer[i], true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Write string to DataView
 */
function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/**
 * Calculate call duration in seconds
 * @param startTime - Start time in milliseconds
 * @param endTime - End time in milliseconds
 * @returns Duration in seconds
 */
export function calculateCallDuration(startTime: number, endTime: number): number {
  return Math.floor((endTime - startTime) / 1000);
}

/**
 * Format duration as MM:SS string
 * @param seconds - Duration in seconds
 * @returns Formatted string (MM:SS)
 */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Create an AudioBuffer from Int16 PCM data
 * @param audioContext - AudioContext to use
 * @param pcmData - Int16 PCM audio data
 * @param sampleRate - Sample rate
 * @returns AudioBuffer ready for playback
 */
export function createAudioBufferFromPCM(
  audioContext: AudioContext,
  pcmData: Int16Array,
  sampleRate: number = 8000
): AudioBuffer {
  const float32Data = int16ToFloat32(pcmData);
  const audioBuffer = audioContext.createBuffer(1, float32Data.length, sampleRate);
  audioBuffer.getChannelData(0).set(float32Data);
  return audioBuffer;
}

/**
 * Mix two Int16 PCM buffers together
 * Used for recording both mic and AI audio
 * @param buffer1 - First PCM buffer
 * @param buffer2 - Second PCM buffer
 * @returns Mixed PCM buffer
 */
export function mixPCMBuffers(buffer1: Int16Array, buffer2: Int16Array): Int16Array {
  const maxLength = Math.max(buffer1.length, buffer2.length);
  const mixed = new Int16Array(maxLength);

  for (let i = 0; i < maxLength; i++) {
    const sample1 = i < buffer1.length ? buffer1[i] : 0;
    const sample2 = i < buffer2.length ? buffer2[i] : 0;
    // Mix with clipping protection
    const mixedSample = Math.max(-32768, Math.min(32767, (sample1 + sample2) / 2));
    mixed[i] = mixedSample;
  }

  return mixed;
}
